import { useEffect, useState } from 'react'
import { Box, styled } from '@mui/material'

const StyledAlbumGalleryPhoto = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'photo' && prop !== 'mode' && prop !== 'isLoaded' && prop !== 'suppressLoadEffect',
})(
  ({
    photo,
    mode,
    isLoaded,
    suppressLoadEffect,
  }: {
    photo: string
    mode: string
    isLoaded: boolean
    suppressLoadEffect?: boolean
  }) => ({
    backgroundImage: `url(${photo})`,
    backgroundSize: mode === 'full' ? 'contain' : 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    height: mode === 'full' ? '80%' : '100%',
    top: mode === 'full' ? '10%' : 0,
    width: '100%',
    position: 'absolute',
    filter: mode === 'full' && !isLoaded && !suppressLoadEffect ? 'blur(12px)' : 'none',
    transform: mode === 'full' && !isLoaded && !suppressLoadEffect ? 'scale(1.05)' : 'scale(1)',
    // transition: 'filter 0.3s ease-out, transform 0.3s ease-out',
  }),
)

interface ProgressiveGalleryPhotoProps {
  lowResSrc: string
  fullResSrc?: string
  mode: string
}

export const ProgressiveGalleryPhoto = ({
  lowResSrc,
  fullResSrc,
  mode,
}: ProgressiveGalleryPhotoProps) => {
  const [loadedSrc, setLoadedSrc] = useState<string>(lowResSrc)
  const [isLoaded, setIsLoaded] = useState(mode !== 'full')

  useEffect(() => {
    if (mode !== 'full' || !fullResSrc) {
      setLoadedSrc(lowResSrc)
      setIsLoaded(true)
      return
    }

    // Start with the low-res placeholder for this photo, then upgrade
    setLoadedSrc(lowResSrc)
    setIsLoaded(false)

    let cancelled = false
    const img = new Image()
    img.src = fullResSrc
    img.onload = () => {
      if (cancelled) return
      setLoadedSrc(fullResSrc)
      setIsLoaded(true)
    }
    img.onerror = () => {
      // Fall back to low-res silently if full-res fails
      if (cancelled) return
      setIsLoaded(true)
    }

    return () => {
      cancelled = true
    }
  }, [mode, lowResSrc, fullResSrc])

  return <StyledAlbumGalleryPhoto photo={loadedSrc} mode={mode} isLoaded={isLoaded} />
}
